import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/Components/ui/dialog';
import { Alert, AlertDescription } from '@/Components/ui/alert';
import { Loader2, X } from 'lucide-react';

interface PaymentProcessingDialogProps {
    isOpen: boolean;
    paymentIntentId: string;
    bookingHash?: string;
    shouldRedirect?: boolean;
    onClose?: () => void;
}

const TIMEOUT_DURATION = 60000; // 1 minute in milliseconds

const PaymentProcessingDialog = ({
    isOpen,
    paymentIntentId,
    bookingHash,
    shouldRedirect = false,
    onClose
}: PaymentProcessingDialogProps) => {
    const [status, setStatus] = useState<'processing' | 'confirmed' | 'error'>('processing');
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

    // Reset status when dialog is opened
    useEffect(() => {
        if (isOpen) {
            setStatus('processing');
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && paymentIntentId) {
            // Listen for payment confirmation event
            const channel = window.Echo.channel(`payment.${paymentIntentId}`);

            channel.listen('PaymentReceived', () => {
                // Clear the timeout immediately
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    setTimeoutId(null);
                }

                setStatus('confirmed');

                // Only redirect if shouldRedirect is true
                if (shouldRedirect && bookingHash) {
                    setTimeout(() => {
                        window.location.href = `/booking/${bookingHash}/thanks`;
                    }, 3000);
                }
            });

            // Set timeout for error state
            const timeout = setTimeout(() => {
                // Only set error if we're still processing
                if (status === 'processing') {
                    setStatus('error');
                }
            }, TIMEOUT_DURATION);

            setTimeoutId(timeout);

            return () => {
                channel.stopListening('PaymentReceived');
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    setTimeoutId(null);
                }
            };
        }

        // Cleanup when dialog closes
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
                setTimeoutId(null);
            }
        };
    }, [isOpen, paymentIntentId, bookingHash, shouldRedirect]);

    // Clear timeout when status changes to confirmed
    useEffect(() => {
        if (status === 'confirmed' && timeoutId) {
            clearTimeout(timeoutId);
            setTimeoutId(null);
        }
    }, [status]);

    const handleOpenChange = (open: boolean) => {
        if (!open && status !== 'processing' && onClose) {
            onClose();
        }
    };

    const gotoPaymentPage = () => {
        window.location.href = `/pi/${paymentIntentId}`;
    }

    const renderContent = () => {
        switch (status) {
            case 'processing':
                return (
                    <>
                        <DialogHeader>
                            <DialogTitle>Processing Your Order</DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-col items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-brand mb-4" />
                            <p className="text-center">Please wait while we process your payment...</p>
                        </div>
                    </>
                );
            case 'confirmed':
                return (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex justify-between items-center">
                                Order Confirmed!
                                <button
                                    onClick={() => onClose?.()}
                                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                >
                                    <X className="h-4 w-4" />
                                    <span className="sr-only">Close</span>
                                </button>
                            </DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-col items-center justify-center py-8">
                            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <p className="text-center">Your payment has been processed successfully!</p>
                            {shouldRedirect ? (
                                <p className="text-center text-sm text-gray-500 mt-2">
                                    Redirecting you to your booking confirmation...
                                </p>
                            ) : (
                                <p className="text-center text-sm text-gray-500 mt-2">
                                    Your gift card details will be emailed to you shortly.
                                </p>
                            )}
                        </div>
                    </>
                );
            case 'error':
                return (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex justify-between items-center">
                                Payment Status Unknown
                                <button
                                    onClick={() => onClose?.()}
                                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                >
                                    <X className="h-4 w-4" />
                                    <span className="sr-only">Close</span>
                                </button>
                            </DialogTitle>
                        </DialogHeader>
                        <Alert variant="destructive" className="mt-4">
                            <AlertDescription>
                                Something went wrong while confirming your payment. Please call our customer service team
                                on 0800 260 6915 to check the status of your order.
                            </AlertDescription>
                        </Alert>
                    </>
                );
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent>
                {renderContent()}
            </DialogContent>
        </Dialog>
    );
};

export default PaymentProcessingDialog;